import instance from './base';

// GET /v1/professionals 的暫存：60 秒內重複呼叫直接回舊資料，
// 同時進行中的請求共用同一個 promise（列表頁一次掛上大量 pro-badge 時避免連發）。
const PROFESSIONAL_LIST_CACHE_MS = 60 * 1000;

let professionalAvatarMap;
let professionalListCache;
let professionalListCachedAt = 0;
let professionalListPending;

export function invalidateProfessionalListCache() {
  professionalListCache = undefined;
  professionalListCachedAt = 0;
}

async function fetchProfessionalList() {
  const rsp = await instance.get('/v1/professionals');
  professionalAvatarMap = {};
  rsp.data.forEach((p) => {
    professionalAvatarMap[p.id] = p.avatar?.url || '';
  });
  professionalListCache = rsp.data;
  professionalListCachedAt = Date.now();
  return rsp.data;
}

// force = true 時略過暫存重新抓取
export async function getProfessionalList(force = false) {
  const cacheValid = professionalListCache !== undefined
    && Date.now() - professionalListCachedAt < PROFESSIONAL_LIST_CACHE_MS;
  if (!force && cacheValid) {
    return [...professionalListCache];
  }
  if (force || !professionalListPending) {
    professionalListPending = fetchProfessionalList().finally(() => {
      professionalListPending = undefined;
    });
  }
  return professionalListPending.then((data) => [...data]);
}

export async function newProfessional(familyName, givenName, email, avatar, publicStatus, title, branches, signature) {
  const rsp = await instance.post('/v1/professionals', {
    family_name: familyName,
    given_name: givenName,
    email,
    avatar,
    public: publicStatus,
    title,
    branches,
    signature,
  });
  invalidateProfessionalListCache();
  return rsp.data;
}

export async function updateProfessional(id, familyName, givenName, email, avatar, publicStatus, title, branches, signature) {
  const rsp = await instance.put(`/v1/professionals/${id}`, {
    family_name: familyName,
    given_name: givenName,
    email,
    avatar,
    public: publicStatus,
    title,
    branches,
    signature,
  });
  invalidateProfessionalListCache();
  return rsp.data;
}

export async function updateProfessionalPublic(id, publicStatus) {
  const rsp = await instance.patch(`/v1/professionals/${id}/public`, {
    public: publicStatus,
  });
  invalidateProfessionalListCache();
  return rsp.data;
}

export async function updateProfessionalStatus(id, activation) {
  const rsp = await instance.patch(`/v1/professionals/${id}/activation`, {
    activated: activation,
  });
  invalidateProfessionalListCache();
  return rsp.data;
}

export async function createAvatar(file) {
  const formData = new FormData();
  formData.append('avatar', file);
  const rsp = await instance.post('/v1/professionals/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return rsp.data;
}

export async function createSignature(file) {
  const formData = new FormData();
  formData.append('signature', file);
  const rsp = await instance.post('/v1/professionals/signature', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return rsp.data;
}

export async function getProfessionalAvatarURL(id) {
  if (professionalAvatarMap === undefined) {
    await getProfessionalList();
  }
  return professionalAvatarMap[id];
}

// 管理員以專業人士身份檢視專業人士平台：簽發一次性 token（僅限 admin）
export async function createImpersonationToken(id) {
  const rsp = await instance.post(`/v1/professionals/${id}/impersonation`);
  return rsp.data;
}

// 管理站行事曆：無權限的預約只回傳時段（occupied），無權限的活動只有名稱與時段（restricted）
export async function getProfessionalCalendar(id, startAt, endAt) {
  const rsp = await instance.get(`/v1/professionals/${id}/calendar`, {
    params: {
      start_at: startAt,
      end_at: endAt,
    },
  });
  return rsp.data;
}

// 專業人士可排案時段設定（管理站）；PUT 為整份覆寫，需帶 version，衝突回 409 availability_conflict
export async function getProfessionalAvailability(id) {
  const rsp = await instance.get(`/v1/professionals/${id}/availability`);
  return rsp.data;
}

export async function updateProfessionalAvailability(id, payload) {
  const rsp = await instance.put(`/v1/professionals/${id}/availability`, payload);
  return rsp.data;
}
